/**
 * ImpersonationDropdown Component
 * Dropdown för global admins att växla till andra användare för support
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth, useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/constants/routes';
import { userMatchesSearch } from '@/utils/userSearch';

interface ImpersonatableUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  rsid?: string | null;
  role: string;
  organization_id: string;
  organization_name: string | null;
}

const ImpersonationDropdown = () => {
  const { t } = useTranslation();
  const { user, impersonation, isImpersonating } = useAuth();
  const { startImpersonation, stopImpersonation } = useAuthActions();
  
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<ImpersonatableUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ImpersonatableUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hämta uppdaterad användarinformation vid mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/api/users/me');
        if (response.data.success && response.data.data) {
          // Uppdatera user-objektet i auth store
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            useAuthStore.setState({
              user: {
                ...currentUser,
                ...response.data.data,
              },
            });
            // Uppdatera även localStorage
            try {
              localStorage.setItem('user', JSON.stringify({
                ...currentUser,
                ...response.data.data,
              }));
            } catch (e) {
              console.error('Error updating user in localStorage:', e);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch current user:', err);
      }
    };

    // Hämta användarinformation om vi har en accessToken
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      fetchCurrentUser();
    }
  }, []);

  // Stäng dropdown vid klick utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hämta användare när dropdown öppnas
  useEffect(() => {
    if (isOpen && users.length === 0 && !isImpersonating) {
      fetchUsers();
    }
  }, [isOpen]);

  // Filtrera användare baserat på sökterm
  useEffect(() => {
    const term = searchTerm.trim();
    if (term) {
      setFilteredUsers(users.filter((u) => userMatchesSearch(u, term)));
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/impersonation/users');
      if (response.data.success) {
        setUsers(response.data.data);
        setFilteredUsers(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError(t('impersonation.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = async (targetUser: ImpersonatableUser) => {
    try {
      setLoading(true);
      const response = await api.post('/api/impersonation/start', {
        targetUserId: targetUser.id,
      });

      if (response.data.success) {
        startImpersonation(
          {
            accessToken: response.data.data.accessToken,
            refreshToken: response.data.data.refreshToken,
            user: response.data.data.user,
          },
          response.data.data.impersonation
        );
        setIsOpen(false);
        // Starta alltid impersonerad användare på Dashboard.
        window.location.assign(ROUTES.DASHBOARD);
      }
    } catch (err: any) {
      console.error('Failed to start impersonation:', err);
      setError(err.response?.data?.error || t('impersonation.startError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    if (!impersonation?.originalUserId) return;

    try {
      setLoading(true);
      const response = await api.post('/api/impersonation/stop', {
        originalUserId: impersonation.originalUserId,
      });

      if (response.data.success) {
        stopImpersonation({
          accessToken: response.data.data.accessToken,
          refreshToken: response.data.data.refreshToken,
          user: response.data.data.user,
        });
        // Reload page för att uppdatera alla komponenter
        window.location.reload();
      }
    } catch (err: any) {
      console.error('Failed to stop impersonation:', err);
      setError(err.response?.data?.error || t('impersonation.stopError'));
    } finally {
      setLoading(false);
    }
  };

  const canImpersonate =
    user?.can_impersonate_users === true ||
    user?.can_impersonate_users === 1 ||
    (typeof user?.can_impersonate_users === 'string' && user.can_impersonate_users === '1');

  if (!canImpersonate && !isImpersonating) {
    return null;
  }

  // Om vi impersonerar, visa bara "Tillbaka"-knappen
  if (isImpersonating) {
    return (
      <div className="flex items-center gap-2">
        {/* Impersonation-indikator */}
        <div className="bg-amber-500/20 text-amber-600 dark:text-amber-400 px-3 py-1 rounded-full text-sm flex items-center gap-2 border border-amber-500/30">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
          <span>
            {t('impersonation.viewingAs')}: {user?.first_name} {user?.last_name}
          </span>
        </div>

        {/* Tillbaka-knapp */}
        <button
          onClick={handleStopImpersonation}
          disabled={loading}
          className="bg-primary hover:bg-primary/80 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1 transition-colors disabled:opacity-50"
          title={t('impersonation.returnToAdmin')}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
            />
          </svg>
          <span>{t('impersonation.returnButton')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger-knapp */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center text-text-default"
        title={t('impersonation.switchUser')}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </button>

      {/* Dropdown-meny */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-bgContainer border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-text-default mb-1">
              {t('impersonation.title')}
            </h3>
            {!(
              user?.is_global_admin === true ||
              user?.is_global_admin === 1 ||
              user?.is_global_admin === '1'
            ) && (
              <p className="text-xs text-text-secondary mb-2">{t('impersonation.partnerSearchHint')}</p>
            )}
            {/* Sökfält */}
            <input
              type="text"
              placeholder={t('impersonation.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-bgLight border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-text-default placeholder-text-secondary"
            />
          </div>

          {/* Användarlista */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-text-secondary">
                {t('common.loading')}
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500 text-sm">{error}</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-text-secondary text-sm">
                {t('impersonation.noUsers')}
              </div>
            ) : (
              <ul className="py-1">
                {filteredUsers.slice(0, 50).map((u) => (
                  <li key={u.id}>
                    <button
                      onClick={() => handleStartImpersonation(u)}
                      className="w-full px-3 py-2 text-left hover:bg-bgLight transition-colors"
                    >
                      <div className="text-sm font-medium text-text-default">
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {u.email}
                      </div>
                      <div className="text-xs text-text-tertiary">
                        {u.organization_name || 'Ingen organisation'} • {u.role || '—'}
                      </div>
                    </button>
                  </li>
                ))}
                {filteredUsers.length > 50 && (
                  <li className="px-3 py-2 text-xs text-text-secondary text-center">
                    {t('impersonation.showingFirst', { count: 50 })} av {filteredUsers.length}
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpersonationDropdown;

